<template>
  <v-app>
    <vue-client-recaptcha v-if="!isDev && !isValid" @isValid="checkValidCaptcha" />
    <span v-else>
      <v-app-bar class="flex-grow-0" color="primary" scroll-behavior="elevate" app>
        <v-app-bar-nav-icon @click.stop="drawer = !drawer">
        </v-app-bar-nav-icon>
        <v-app-bar-title>
          <strong>chatSO</strong>
          <span v-if="modelInfo.name" class="model-info">
            · {{ modelInfo.name }}<span v-if="modelInfo.params"> ({{ modelInfo.params }})</span><span v-if="modelInfo.domain"> · (inférences sur {{ modelInfo.domain }})</span>
          </span>
        </v-app-bar-title>
      </v-app-bar>
      <v-navigation-drawer elevation="3" app v-model="drawer">
        <v-list density="compact">
          <v-list-subheader>
            <div @click="startNewConversation" style="cursor: pointer;" title="Nouvelle conversation">
              <v-img>
                <img src="./assets/chatso.jpg" alt="Mon image" style="height: 100px" class="img-chatso" />
              </v-img>
            </div>
          </v-list-subheader>
          <v-divider></v-divider>
          <v-list-item v-for="(item, i) in items" :key="i" :value="item" class="p-3" @click="handleMenuClick(item)">
            <template v-slot:prepend>
              <v-icon class="me-3 ms-3"> {{ item.icon }} </v-icon>
            </template>
            <router-link v-if="item.url" :to="item.url" style="color: hsl(210, 8%, 35%); text-decoration: none">
              <v-list-item-title v-text="item.title"></v-list-item-title>
            </router-link>
            <v-list-item-title v-else v-text="item.title" style="color: hsl(210, 8%, 35%); cursor: pointer"></v-list-item-title>
          </v-list-item>
        </v-list>
      </v-navigation-drawer>
      
      <!-- History dialog -->
      <v-dialog v-model="showHistory" max-width="600" scrollable>
        <ConversationHistory 
          :conversations="savedConversations"
          :current-conversation-id="currentConversationId"
          @load-conversation="loadConversationFromHistory"
          @delete-conversation="deleteConversationFromHistory"
        />
      </v-dialog>
      
      <!-- Sizes your content based upon application components -->
      <v-main>
        <div class="container">
          <router-view 
            :drawer="drawer" 
            @close-drawer="drawer = false"
            @update-conversations="updateConversations"
          />
        </div>
      </v-main>

      <v-footer app>
        <!-- -->
      </v-footer>
    </span>
  </v-app>
</template>

<script setup>
import { ref, provide, onMounted } from "vue";
import VueClientRecaptcha from "@/components/VueClientRecaptcha.vue";
import ConversationHistory from "@/components/ConversationHistory.vue";
import { mdiFileDocument, mdiInformation, mdiChat, mdiFlag, mdiHistory } from "@mdi/js";
import Papa from 'papaparse';       // csv parser
import config from "@/config/config.js";

// Drawer starts closed on mobile, open on desktop
const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
const drawer = ref(!isMobile)

// Check if captcha was completed within the last hour
const checkCaptchaSession = () => {
  const captchaData = localStorage.getItem('chatso_captcha');
  if (captchaData) {
    try {
      const { isValid: storedValid, timestamp } = JSON.parse(captchaData);
      const oneHour = 60 * 60 * 1000; // 1 hour in milliseconds
      const now = Date.now();
      
      if (storedValid && (now - timestamp) < oneHour) {
        return true;
      }
    } catch (e) {
      console.error('Error parsing captcha session:', e);
    }
  }
  return false;
};

// Bypass captcha - always set to true
const isValid = ref(true) // ref(checkCaptchaSession())
const modelInfo = ref({ name: '', params: '', domain: '' })
const showHistory = ref(false)
const savedConversations = ref([])
const currentConversationId = ref(null)

const items = [
  { title: "Chat", icon: mdiChat, url: "/" },
  { title: "Historique", icon: mdiHistory, action: 'history' },
  { title: "Ressources", icon: mdiFileDocument, url: "/Ressources" },
  { title: "À propos", icon: mdiInformation, url: "/About" },
  { title: "Version", icon: mdiFlag, url: "/Version" },
]

// Load saved conversations from localStorage
const loadSavedConversations = () => {
  try {
    const conversationsData = localStorage.getItem('chatso_conversations');
    if (conversationsData) {
      const allConversations = JSON.parse(conversationsData);
      const now = new Date();
      
      // Filter conversations less than 30 days old
      savedConversations.value = allConversations.filter(conv => {
        const convDate = new Date(conv.timestamp);
        const daysDiff = (now - convDate) / (1000 * 60 * 60 * 24);
        return daysDiff < 30;
      }).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    }
  } catch (error) {
    console.error('Error loading saved conversations:', error);
    savedConversations.value = [];
  }
};

const handleMenuClick = (item) => {
  if (item.action === 'history') {
    loadSavedConversations();
    showHistory.value = true;
  }
};

const loadConversationFromHistory = (conversationId) => {
  currentConversationId.value = conversationId;
  showHistory.value = false;
  drawer.value = false;
  // Emit event to router-view to load conversation
  window.dispatchEvent(new CustomEvent('load-conversation', { detail: conversationId }));
};

const deleteConversationFromHistory = (conversationId) => {
  try {
    savedConversations.value = savedConversations.value.filter(c => c.id !== conversationId);
    localStorage.setItem('chatso_conversations', JSON.stringify(savedConversations.value));
    // Emit event to router-view to handle deletion
    window.dispatchEvent(new CustomEvent('delete-conversation', { detail: conversationId }));
  } catch (error) {
    console.error('Error deleting conversation:', error);
  }
};

const updateConversations = (data) => {
  if (data && data.conversations) {
    savedConversations.value = data.conversations;
  }
  if (data && data.currentId !== undefined) {
    currentConversationId.value = data.currentId;
  }
};

const startNewConversation = () => {
  drawer.value = false;
  window.dispatchEvent(new CustomEvent('new-conversation'));
};

const configCSVFile = "ressources.tsv"
// const configCSVFile = "/api/ateliersdeladonneesgt1/chatbot-recherche/-/raw/dev/frontend/public/ressources.tsv?ref_type=heads";

const configLinks = ref([])
provide('configLinks', configLinks)

const loadConfig = () => {
  const configCSVFileUrl = new URL(configCSVFile, window.location.href).href
  Papa.parse(configCSVFileUrl, {
    download: true,                               // file is an url
    header: true,
    delimiter: '\t',                              // tab separated values
    skipEmptyLines: true,
    comments: '#',                               // ignore lines starting with #
    complete: async function (results) {
      configLinks.value = results.data
    },
    error: function (error, file) {
      console.log("Erreur lors du csv parse:", error, file);
    },
  });
}

loadConfig()

// Fetch model information from backend
const fetchModelInfo = async () => {
  try {
    const response = await fetch(`${config.apiUrl}/version`);
    const data = await response.json();
    
    // Model parameter counts (approximate)
    const modelParams = {
      'qwen3': '14B',
      'gpt-oss': '8B',
      'lucie': '70B',
      'mistral-small': '22B'
    };
    
    // Extract inference domain from the api_base returned by the backend /version endpoint
    let domain = '';
    try {
      const inferenceUrl = data.api_base || '';
      if (inferenceUrl) {
        domain = new URL(inferenceUrl).hostname;
      }
    } catch (e) {
      console.error('Failed to extract inference domain:', e);
    }
    
    modelInfo.value = {
      name: data.model || 'Unknown',
      params: modelParams[data.model] || '',
      domain: domain
    };
  } catch (error) {
    console.error('Failed to fetch model info:', error);
  }
};

onMounted(() => {
  fetchModelInfo();
});

const isDev = process.env.NODE_ENV === "development";

const checkValidCaptcha = (value) => {
  isValid.value = value;
  
  // Store captcha validation in localStorage with timestamp
  if (value) {
    const captchaData = {
      isValid: true,
      timestamp: Date.now()
    };
    localStorage.setItem('chatso_captcha', JSON.stringify(captchaData));
  }
};

</script>


<style>
.model-info {
  font-size: 0.85em;
  opacity: 0.85;
  font-weight: 400;
  margin-left: 4px;
}

.img-chatso {
  height: 70px !important;
  margin: auto;

  padding: 15px 15px;
  background: white;
  clip-path: ellipse(50% 50%);

}

.v-list-subheader__text {
  transition: all 0.2s ease-in-out;

  &:hover {
    filter: brightness(0.95);
  }

  padding: 5px 5px;
  background: #0000000d;
  margin: 0px 60px;
  clip-path: ellipse(50% 50%);
}

.v-list-item:has(a.router-link-active) {
  background: rgba(0, 0, 0, .087) !important;
}

.v-list-item--variant-text .v-list-item__overlay {
  background: none !important;
}

/* .v-list-item--active .v-list-item__prepend > .v-icon, 
.v-list-item--active .v-list-item__append > .v-icon {
    opacity: 1;
} */
</style>